import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout/Layout';

const RoleConfigManager = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mergedConfig, setMergedConfig] = useState({ roleHierarchy: {}, permissions: {} });
  const [tenantConfig, setTenantConfig] = useState({ roleHierarchy: {}, permissions: {} });

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const [mergedRes, tenantRes] = await Promise.all([
        axios.get('/api/tenant-roles/roles/config'),
        axios.get('/api/tenant-roles/roles/config/tenant')
      ]);
      if (mergedRes.data?.success) setMergedConfig(mergedRes.data.config || { roleHierarchy: {}, permissions: {} });
      if (tenantRes.data?.success) setTenantConfig(tenantRes.data.config || { roleHierarchy: {}, permissions: {} });
    } catch (e) {
      toast.error('Failed to load role configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConfigs(); }, []);

  const handleAddRole = () => {
    const roleName = prompt('Enter new role name');
    if (!roleName) return;
    setTenantConfig(prev => ({
      roleHierarchy: { ...prev.roleHierarchy, [roleName]: 10 },
      permissions: { ...prev.permissions, [roleName]: [] }
    }));
  };

  const handleSetLevel = (role, level) => {
    setTenantConfig(prev => ({
      ...prev,
      roleHierarchy: { ...prev.roleHierarchy, [role]: Number(level) }
    }));
  };

  const handleTogglePermission = (role, perm) => {
    setTenantConfig(prev => {
      const current = new Set(prev.permissions[role] || []);
      if (current.has(perm)) current.delete(perm); else current.add(perm);
      return { ...prev, permissions: { ...prev.permissions, [role]: Array.from(current) } };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put('/api/tenant-roles/roles/config/tenant', tenantConfig);
      toast.success('Role configuration saved');
      await loadConfigs();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">Loading...</div>
      </Layout>
    );
  }

  const allRoles = Array.from(new Set([
    ...Object.keys(mergedConfig.roleHierarchy || {}),
    ...Object.keys(tenantConfig.roleHierarchy || {}),
    ...Object.keys(mergedConfig.permissions || {}),
    ...Object.keys(tenantConfig.permissions || {})
  ]));

  const allPermissions = Array.from(new Set(
    Object.values(mergedConfig.permissions || {}).flat()
  ));

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Role Configuration</h1>
          <div className="space-x-2">
            <button onClick={handleAddRole} className="px-3 py-2 bg-gray-700 text-white rounded">Add Role</button>
            <button onClick={handleSave} disabled={saving} className="px-3 py-2 bg-blue-600 text-white rounded disabled:opacity-50">{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2">Role</th>
                <th className="text-left p-2">Level (tenant override)</th>
                {allPermissions.map(perm => (
                  <th key={perm} className="text-left p-2 whitespace-nowrap">{perm}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allRoles.map(role => (
                <tr key={role} className="border-t border-gray-200">
                  <td className="p-2 font-medium">{role}</td>
                  <td className="p-2">
                    <input
                      type="number"
                      className="border rounded px-2 py-1 w-24"
                      value={tenantConfig.roleHierarchy?.[role] ?? ''}
                      onChange={e => handleSetLevel(role, e.target.value)}
                      placeholder={mergedConfig.roleHierarchy?.[role] ?? ''}
                    />
                  </td>
                  {allPermissions.map(perm => {
                    const mergedHas = (mergedConfig.permissions?.[role] || []).includes(perm);
                    const tenantSet = new Set(tenantConfig.permissions?.[role] || []);
                    const effectiveHas = tenantSet.size > 0 ? tenantSet.has(perm) : mergedHas;
                    return (
                      <td key={perm} className="p-2">
                        <input
                          type="checkbox"
                          checked={effectiveHas}
                          onChange={() => handleTogglePermission(role, perm)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default RoleConfigManager;

